<template>
  <AddDialogWithLoader
    :dialog-title="$route.meta.title"
    :confirm-disabled="disableConfirm"
    :scroll="scrollAmount"
    :sm-sizes="[30, 30, 40]"
    :md-sizes="[30, 30, 40]"
    :lg-sizes="[30, 30, 40]"
    :xl-sizes="[30, 30, 40]"
    :items-sizes="[100, 100, 100, 50]"
    items-panel="left"
    @add="add"
    @scroll="scrolled"
  >
    <template #header>
      <div>
        <PbInput
          v-model="searchInput"
          dynamic
          :label="translate('pb2.addChannelView.searchLabel')"
        >
          <BaseButton
            :class="{
              'txt-muted': !searchInput,
              'hover-error': !!searchInput,
              'focus-error': true,
            }"
            :disabled="!searchInput"
            @click="clearSearch"
          >
            <UiIcon
              icon="trash"
              size="small"
            />
          </BaseButton>
        </PbInput>
      </div>
    </template>

    <template
      v-if="channels.length"
      #leftTitle
    >
      <span class="pb-add-dialog-panel-header-title">
        {{ translate('pb2.addChannelView.availableChannelsTitle') }}
        <template v-if="filteredChannels.length < channels.length">
          ({{ filteredChannels.length }}/{{ channels.length }})
        </template>
      </span>
      <ButtonWithLoader
        v-if="!isGlobal"
        :label="translate('pb2.addChannelView.selectAllLabel')"
        @click="selectAll"
      />
    </template>
    <template
      v-if="channels.length"
      #left
    >
      <PbDefaultItem
        v-for="channel in filteredChannels"
        :key="`${channel.channel_type}_${channel.name}`"
        v-memo="[`${channel.channel_type}_${channel.name}`]"
        :item="channel"
        :show-beams="!isGlobal"
        :action="translate('pb2.addChannelView.selectTooltip')"
        @click="select"
      />
      <span
        v-if="searchInput && !filteredChannels.length"
        class="pb-add-dialog-after-results"
      >
        {{ translate('pb2.addChannelView.noResultsMessage') }}
      </span>
    </template>
    <template
      v-else
      #left
    >
      <p>
        {{ translate('pb2.addChannelView.templateChannelsNeededPrefix') }}
        <PbLink
          :to="{
            name: routeNames.AddGlobalChannel,
            // Move the required params to query parameters,
            // so that they're accessible in the dialog
            query: {
              channelUid: profileStore.channelUid,
            },
          }"
        >
          {{ translate('pb2.addChannelView.templateChannelsNeededLink') }}
        </PbLink>
      </p>
    </template>

    <template #middleTitle>
      <span class="pb-add-dialog-panel-header-title">{{ translate('pb2.addChannelView.selectedTitle') }}</span>
      <ButtonWithLoader
        v-if="selectedChannels.length"
        :label="translate('pb2.addChannelView.deselectAllLabel')"
        @click="deselectAll"
      />
    </template>
    <template
      v-if="channels.length"
      #middle
    >
      <template v-if="selectedChannels.length">
        <div
          v-for="(repetition, repetitionIndex) in repetitions"
          :key="`repeat_${repetitionIndex}`"
          class="pb-add-channel-dialog-repeat"
        >
          <template
            v-for="(selection, selectionIndex) in repetition"
            :key="`${selection.channel.channel_type}_${selection.channel.name}_${selectionIndex}`"
          >
            <PbDefaultItem
              v-if="repetitionIndex === 0"
              :item="selection.channel"
              show-beams
              :action="translate('pb2.addChannelView.deselectTooltip')"
              @click="deselect(selectionIndex)"
            />
            <PbDefaultItem
              v-else
              :item="selection.channel"
              show-beams
              disabled
            />
          </template>
        </div>
      </template>
      <div v-else>
        {{ translate('pb2.addChannelView.noSelectionMessage') }}
      </div>
    </template>

    <template
      v-if="channels.length"
      #right
    >
      <UiField
        :label="translate('pb2.addChannelView.repeatCounterLabel')"
        class="pb-half-margin-bottom"
        nofocus
        nolabel
      >
        <ControlCounter
          v-model="repeat"
          :label="translate('pb2.addChannelView.repeatCounterLabel')"
          :editable="true"
          :min="1"
          :max="maxRepeat"
        />
      </UiField>
      <div class="pb-add-channel-dialog-options-accordion accordion">
        <BaseCollapse
          v-bind="collapses.location.control"
          :title="translate('pb2.addChannelView.locationCollapseTitle')"
        >
          <Radio
            v-model="positionOption"
            :options="positionOptions"
          />
        </BaseCollapse>
        <BaseCollapse
          v-bind="collapses.name.control"
          :title="translate('pb2.addChannelView.nameCollapseTitle')"
        >
          <Radio
            v-model="nameOption"
            :options="nameOptions"
          />
          <template v-if="nameOption.id === NAME_INCREMENTAL">
            {{ translate('pb2.addChannelView.incrementalNameStartFromShortLabel') }}
            <ControlCounter
              v-model="startNameFrom"
              :label="translate('pb2.addChannelView.incrementalNameStartFromFullLabel')"
              :editable="true"
              :min="1"
            />
          </template>
        </BaseCollapse>
        <BaseCollapse
          v-bind="collapses.beams.control"
          :title="translate('pb2.addChannelView.beamsCollapseTitle')"
        >
          <Radio
            v-model="beamsOption"
            :options="beamsOptions"
          />
          <BeamsSelector
            v-if="beamsOption.id === BEAMS_CUSTOM"
            v-model:selection="beams"
            class="pb-add-channel-dialog-beams-selection"
          />
          <template v-if="beamsOption.id === BEAMS_INCREMENTAL">
            {{ translate('pb2.addChannelView.incrementalBeamStartFromShortLabel') }}
            <ControlCounter
              v-model="startBeamsFrom"
              :label="translate('pb2.addChannelView.incrementalBeamStartFromFullLabel')"
              :editable="true"
              :min="1"
            />
          </template>
        </BaseCollapse>
      </div>
    </template>
  </AddDialogWithLoader>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import UiIcon from '@nicolaudie-group/ng-ui-kit/components/UiIcon/UiIcon.vue'
import BaseButton from '@nicolaudie-group/ng-ui-kit/components/BaseButton/BaseButton.vue'
import BaseCollapse from '@nicolaudie-group/ng-ui-kit/components/BaseCollapse/BaseCollapse.vue'
import ControlCounter from '@nicolaudie-group/ng-ui-kit/components/ControlCounter/ControlCounter.vue'
import UiField from '@nicolaudie-group/ng-ui-kit/components/UiField/UiField.vue'
import Radio from '@nicolaudie-group/ng-ui-kit/components/Radio/Radio.vue'
import useChannelStore from '@/stores/channel.store'
import useProfileStore from '@/stores/profile.store'
import AddDialogWithLoader from '@/components/AddDialog/AddDialogWithLoader.vue'
import PbInput from '@/components/PbInput.vue'
import useIsGlobal from '@/composables/useIsGlobal'
import PbDefaultItem from '@/components/PbDefaultItem/PbDefaultItem.vue'
import { channelHelpers } from '@/helpers/profile'
import routeNames from '@/router/names'
import ButtonWithLoader from '@/components/ButtonWithLoader.vue'
import PbLink from '@/components/PbLink.vue'
import {
  useBeams,
  useChannel,
  useConstants,
  useGlobalChannels,
} from '@/composables/useProfile'
import useDialogSearch from '@/composables/useDialogSearch'
import useAccordion from '@/composables/useAccordion'
import useTranslate from '@/composables/useTranslate'
import BeamsSelector from '@/components/Beams/BeamsSelector.vue'

const POSITION_BEFORE_ALL = 'beforeAll'
const POSITION_BEFORE_SELECTION = 'beforeSelection'
const POSITION_AFTER_SELECTION = 'afterSelection'
const POSITION_AFTER_ALL = 'afterAll'

const NAME_UNCHANGED = '%{name}'
const NAME_INCREMENTAL = '%{index}. %{name}'

const BEAMS_UNCHANGED = 'unchanged'
const BEAMS_INCREMENTAL = 'incremental'
const BEAMS_CUSTOM = 'custom'

const { isGlobal } = useIsGlobal()

const { translate } = useTranslate()

const channelStore = useChannelStore()
const profileStore = useProfileStore()

const selectedChannels = ref([])
const positionOption = ref({ id: POSITION_AFTER_ALL })
const nameOption = ref({ id: NAME_UNCHANGED })
const startNameFrom = ref(1)
const beamsOption = ref({ id: BEAMS_UNCHANGED })
const beams = ref(null)
const startBeamsFrom = ref(1)

const currentChannel = useChannel()
const constants = useConstants()
const globalChannels = useGlobalChannels()

const positionOptions = computed(() => {
  const result = [
    {
      id: POSITION_BEFORE_ALL,
      title: translate('pb2.addChannelView.beforeAllOptionTitle'),
      description: translate('pb2.addChannelView.beforeAllOptionDescription'),
    },
    {
      id: POSITION_BEFORE_SELECTION,
      title: translate('pb2.addChannelView.beforeSelectionOptionTitle'),
      disabled: !currentChannel.value,
      description: (
        currentChannel.value
          ? `${translate('pb2.addChannelView.beforeSelectionOptionDescription')} "${currentChannel.value.linkName}"`
          : translate('pb2.addChannelView.noSelectionOptionDescription')
      ),
    },
    {
      id: POSITION_AFTER_SELECTION,
      title: translate('pb2.addChannelView.afterSelectionOptionTitle'),
      disabled: !currentChannel.value,
      description: (
        currentChannel.value
          ? `${translate('pb2.addChannelView.afterSelectionOptionDescription')} "${currentChannel.value.linkName}"`
          : translate('pb2.addChannelView.noSelectionOptionDescription')
      ),
    },
    {
      id: POSITION_AFTER_ALL,
      title: translate('pb2.addChannelView.afterAllOptionTitle'),
      description: translate('pb2.addChannelView.afterAllOptionDescription'),
    },
  ]
  return result
})

const repeat = ref(1)

const nameOptions = computed(() => [
  {
    id: NAME_UNCHANGED,
    title: translate('pb2.addChannelView.unchangedNameOptionTitle'),
    description: translate('pb2.addChannelView.unchangedNameOptionDescription'),
  },
  {
    id: NAME_INCREMENTAL,
    title: translate('pb2.addChannelView.incrementalNameOptionTitle'),
    description: translate('pb2.addChannelView.incrementalNameOptionDescription'),
    disabled: repeat.value < 2,
  },
])

const beamsOptions = computed(() => {
  const origin = (
    isGlobal.value
      ? translate('pb2.addChannelView.defaultBeamsOptionOriginAllBeams')
      : translate('pb2.addChannelView.defaultBeamsOptionOriginTemplate')
  )

  return [
    {
      id: BEAMS_UNCHANGED,
      title: translate('pb2.addChannelView.defaultBeamsOptionTitle'),
      description: `${translate('pb2.addChannelView.defaultBeamsOptionDescription')} (${origin})`,
    },
    {
      id: BEAMS_INCREMENTAL,
      title: translate('pb2.addChannelView.incrementalBeamsOptionTitle'),
      description: translate('pb2.addChannelView.incrementalBeamsOptionDescription'),
      disabled: repeat.value < 2,
    },
    {
      id: BEAMS_CUSTOM,
      title: translate('pb2.addChannelView.customBeamsOptionTitle'),
      description: translate('pb2.addChannelView.customBeamsOptionDescription'),
    },
  ]
})

const {
  searchInput,
  searchQuery,
  clear: clearSearch,
  scrolled,
  scrollAmount,
} = useDialogSearch()

const channels = computed(() => {
  const defaultChannels = (
    constants.value
      ? constants.value.channels.default
      : []
  )
  const globalChannelsData = (globalChannels.value || []).map((channel) => channel.data)
  return isGlobal.value ? defaultChannels : globalChannelsData
})

const filteredChannels = computed(() => {
  const channelsList = channels.value
  const searchString = searchQuery.value
  if (searchString) {
    return channelsList.filter((channel) => channel.searchIndex.includes(searchString))
  }
  return channelsList
})

/**
 * Limit the number of repeats based on the number of channels to repeat,
 * so that the profile don't get more than the maximum of 512 channels in one universe
 */
// TODO: also take into account the number of channels in the current mode for local channels
// TODO: take number of modes into account for global channels, as each mode can have 512 channels
const maxRepeat = computed(() => {
  if (selectedChannels.value.length) {
    return Math.floor(512 / selectedChannels.value.length)
  }
  return 512
})

const disableConfirm = computed(() => !selectedChannels.value.length)

function getOrderFromPosition(index, repetition, channelsPerRepetition) {
  switch (positionOption.value.id) {
    case POSITION_BEFORE_ALL:
      return (repetition * channelsPerRepetition) + index
    case POSITION_BEFORE_SELECTION:
      return currentChannel.value.data._order + (repetition * channelsPerRepetition) + index
    case POSITION_AFTER_SELECTION:
      return currentChannel.value.data._order + (repetition * channelsPerRepetition) + index + 1
    case POSITION_AFTER_ALL:
    default:
      return undefined
  }
}

const { collapses } = useAccordion([
  'location',
  'name',
  'beams',
], { query: 'setting', initial: 'location' })

watch(repeat, (newRepeat) => {
  if (newRepeat < 2) {
    nameOption.value = nameOptions.value[0]
    beamsOption.value = beamsOptions.value[0]
  }
})

const profileBeams = useBeams()

const repetitions = computed(() => {
  const reps = []
  const selected = selectedChannels.value
  const repeatTimes = repeat.value
  const globalNameTemplate = nameOption.value.id
  const customBeams = beams.value
  const selectedBeamsOption = beamsOption.value.id
  const profileBeamsArray = [...profileBeams.value || []].sort((a, b) => a.id - b.id)
  const startName = startNameFrom.value
  const startBeams = startBeamsFrom.value

  for (let i = 0; i < repeatTimes; i += 1) {
    const rep = selected.map(({ channel, options }, index) => {
      let nameTemplate = NAME_UNCHANGED
      if (options.name) {
        nameTemplate = options.name
      }
      if (globalNameTemplate) {
        nameTemplate = globalNameTemplate
      }
      const name = nameTemplate.template({ index: startName + i, name: channel.name })

      let channelBeams
      if (selectedBeamsOption === BEAMS_CUSTOM) {
        channelBeams = customBeams || []
        if (channelBeams.length === profileBeamsArray.length) {
          channelBeams = []
        }
      } else if (selectedBeamsOption === BEAMS_INCREMENTAL) {
        const beamId = profileBeamsArray[startBeams - 1 + i]?.id
        if (beamId) {
          channelBeams = [{
            beam_index: beamId,
          }]
        }
      }

      const populatedChannel = {
        ...channel,
        name,
        beams: channelBeams,
        _order: getOrderFromPosition(index, i, selected.length),
      }

      return {
        options,
        channel: populatedChannel,
      }
    })
    reps.push(rep)
  }

  return reps
})

function flattenRepetitions() {
  const result = []
  const repetitionsList = repetitions.value
  for (let i = 0; i < repetitionsList.length; i += 1) {
    const repetition = repetitionsList[i]
    for (let j = 0; j < repetition.length; j += 1) {
      const selection = repetition[j]
      result.push({
        ...selection,
        repetition: i,
      })
    }
  }
  return result
}

function add() {
  const toAdd = flattenRepetitions()
  if (isGlobal.value) {
    return channelStore.createGlobal({
      channels: toAdd.map(({ channel }) => ({
        _order: channel._order,
        channel_type: channel.channel_type,
        name: channel.name,
        beams: channel.beams,
        presetRanges: (
          channelHelpers.canHavePresets(channel)
            ? [{}]
            : undefined
        ),
      })),
    })
  }
  return channelStore.createLocal({
    channels: toAdd.map(({ channel }) => ({
      _order: channel._order,
      name: channel.name,
      beams: channel.beams,
      _linkedChannel: channel._uid,
    })),
  })
}

function createSelection(channel) {
  return {
    options: {},
    channel,
  }
}

function select(channel) {
  selectedChannels.value.push(createSelection(channel))
}

function selectAll() {
  selectedChannels.value.push(
    ...channels.value.map(
      (channel) => createSelection(channel),
    ),
  )
}

function deselect(idx) {
  selectedChannels.value.splice(idx, 1)
}

function deselectAll() {
  selectedChannels.value = []
}
</script>

<style lang="scss" src="./AddChannelDialog.scss" />
